import { useState, type ReactNode } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { reportingDevice } from '~/modules/tracking/util/reporting-device';
import { useGetMatchedPromptImpression } from '~/modules-rocco/get-matched/use-prompt-impression';
import {
    GetMatchedExplainerModal,
    type GetMatchedExplainer,
} from '~/modules-rocco/get-matched/GetMatchedExplainerModal';
import type { GetMatchedEntryPoint } from '~/state/ui-state-get-matched-modal';
import { useOpenGetMatchedModal } from '~/state/ui-state-get-matched-modal';
import { useDirectoryBrowseTheme } from './directory-browse-theme';
import { DARK_SELECTED } from '~/ui-rocco/button-system';

interface DirectoryIntroProps {
    /** Small grey label above the heading, e.g. "Products Library". */
    eyebrow: string;
    /** Editorial intro copy: the hero the loader derived, at most three lines. */
    heading: string;
    /**
     * The heading is a `?previewField=` preview, not the CMS copy (local dev
     * and the dev/team hosts). Nothing visible changes — the point of the preview is to see the
     * text exactly as it will ship — but a screen-reader status names it.
     */
    headingPreviewed?: boolean;
    /**
     * Active category name (e.g. "Commercial"). On a category page it's shown
     * bold before the heading ("Commercial - {heading}"), mirroring the legacy
     * TitleSection so the category is named even when the description falls back
     * to the generic root copy. Omit at the root.
     */
    categoryPrefix?: string;
    /**
     * Mobile presentation. 'page-lead' (default) treats the copy as quiet meta
     * and hides the Get Matched rail entirely — the grid injections carry that
     * CTA on mobile. 'grid-injection' keeps the pitch prominent with the
     * Get Matched button, hiding only the plus icon. Desktop is identical in
     * both variants.
     */
    variant?: 'page-lead' | 'grid-injection';
    /**
     * Hide the rail's "Can't find it?" caption below md. On desktop the
     * caption annotates the far-right rail; stacked under the statement on
     * mobile the two read as repetition, so hosts whose layout stacks them
     * (the detail pages' closing module) drop it there.
     */
    hideMobileCaption?: boolean;
    /**
     * Drop the display heading below md.
     *
     * With a `categoryPrefix`, mobile names the category inline at the head of
     * the statement instead — `{category} - {description}`, as desktop does —
     * and that inline title is the page's h1 at every width. Without one, the
     * display heading stays for screen readers only: a page whose only h1 is
     * deleted at one width announces itself as nothing there.
     */
    hideMobileHeading?: boolean;
    /**
     * Set the whole statement in the page ink below md, rather than the
     * first sentence in ink and the remainder in the pale tail. Hosts that
     * treat this copy as the page's description — not as a statement with a
     * quiet aside — read better in one tone.
     */
    mobileSingleTone?: boolean;
    /** Drop the injection variant's eyebrow below md. */
    hideMobileEyebrow?: boolean;
    /**
     * Rail caption copy. The default names suppliers (the directory grids'
     * audience); the project detail closing swaps in professionals to match
     * the flow its brief opens.
     */
    caption?: string;
    /**
     * Which prompt this is, for reporting. Not derivable from `variant`: the
     * detail pages' closing band renders the injection variant too, and the two
     * are different prompts in different places competing for the same brief.
     */
    entryPoint: GetMatchedEntryPoint;
    /**
     * The statement's face, where a page sets it in something other than the
     * grotesk — the articles directory, which speaks in the editorial serif.
     */
    statementClassName?: string;
    /**
     * The statement's measure on desktop. The directories set theirs to three
     * lines; the articles directory sets its sentence wider, on two.
     */
    statementMeasureClassName?: string;
    /**
     * What stands beside the statement instead of the Get Matched prompt —
     * the articles directory offers its publications there. Drawn with
     * {@link INTRO_RAIL_CAPTION}, {@link INTRO_RAIL_DISC} and
     * {@link INTRO_RAIL_BUTTON} so it sits exactly where the prompt would.
     */
    rail?: ReactNode;
    /**
     * What the rail's plus opens: a word on what Get Matched is, with
     * Continue leading on to the brief. Without it the plus opens the brief
     * directly, as the labelled button beside it always does.
     */
    explainer?: GetMatchedExplainer;
}

/** The rail's caption, ranged right against the disc. */
export const INTRO_RAIL_CAPTION =
    'text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.02em] text-[#767676] md:w-[20.0625rem] md:text-right';

/** The rail's round control beside the caption. */
export const INTRO_RAIL_DISC =
    'relative flex size-[2.625rem] shrink-0 items-center justify-center rounded-[var(--radius-btn-m)]';

/** The rail's labelled button under the caption. */
export const INTRO_RAIL_BUTTON =
    'flex h-[3.25rem] w-[11.625rem] items-center justify-center rounded-[1.375rem] px-[1.625rem] py-3 text-[1rem] leading-[1.3] font-medium tracking-[0.02rem]';

/**
 * The rail's plus: the design's light "times" cross rotated -45° into a plus,
 * which gives it longer, more elegant arms than a geometric plus.
 */
export const IntroPlusGlyph = () => (
    <svg
        viewBox="0 0 23.6048 23.6048"
        fill="none"
        aria-hidden="true"
        className="size-[1.475rem] -rotate-45"
    >
        <path
            d="M13.4718 11.8027L18.5155 6.75893L19.5556 5.71881C19.709 5.56538 19.709 5.31604 19.5556 5.16261L18.4427 4.0497C18.2893 3.89627 18.0399 3.89627 17.8865 4.0497L11.8027 10.1335L5.71881 4.04921C5.56538 3.89578 5.31604 3.89578 5.16261 4.04921L4.04921 5.16212C3.89578 5.31555 3.89578 5.56489 4.04921 5.71832L10.1335 11.8027L4.04921 17.8865C3.89578 18.0399 3.89578 18.2893 4.04921 18.4427L5.16212 19.5556C5.31555 19.709 5.56489 19.709 5.71832 19.5556L11.8027 13.4718L16.8464 18.5155L17.8865 19.5556C18.0399 19.709 18.2893 19.709 18.4427 19.5556L19.5556 18.4427C19.709 18.2893 19.709 18.0399 19.5556 17.8865L13.4718 11.8027Z"
            fill="currentColor"
        />
    </svg>
);

/**
 * Directory browse intro: eyebrow + editorial heading on the left, and the
 * "Get Matched" prompt + button on the right. Matches the 5.0 category-page
 * design. The buttons open the Get Matched modal, which the revamp browse
 * routes mount (entity-typed) — without a mounted modal (e.g. the legacy
 * browse grid's injections) the click is a no-op and the intro stays
 * presentational.
 */
export const DirectoryIntro = ({
    eyebrow,
    heading,
    headingPreviewed = false,
    categoryPrefix,
    variant = 'page-lead',
    hideMobileCaption = false,
    hideMobileHeading = false,
    mobileSingleTone = false,
    hideMobileEyebrow = false,
    caption = "Can't find it? Describe what you need and matching suppliers will come to you.",
    entryPoint,
    statementClassName,
    statementMeasureClassName = 'md:max-w-[60.375rem]',
    rail,
    explainer,
}: DirectoryIntroProps) => {
    const onGetMatched = useOpenGetMatchedModal(entryPoint);
    const [explainerOpen, setExplainerOpen] = useState(false);
    const tracker = useTracker();
    // Keyed on the entry point, as the prompt's impression is, so the opens
    // read against the same denominator.
    const openExplainer = () => {
        setExplainerOpen(true);
        const payload = { entryPoint, device: reportingDevice() };
        void tracker.log('GetMatchedExplainerOpened', {
            url: new URL(window.location.href),
            data: { Source: entryPoint, ExtraData: JSON.stringify(payload) },
        });
    };
    const onPlus = explainer ? openExplainer : onGetMatched;
    const promptRef = useGetMatchedPromptImpression(entryPoint);
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const isInjection = variant === 'grid-injection';
    const inlineMobileTitle = hideMobileHeading && !!categoryPrefix;
    // Mobile lead label: the category name on child pages, otherwise the
    // vertical ("Products Library" → "Products"), homepage "(About)" style.
    const introLabel = categoryPrefix ?? eyebrow.replace(/\s*Library\s*$/i, '');
    // First sentence carries the ink; the remainder reads as the pale tail
    // (the logged-out homepage statement treatment).
    const sentenceBreak = heading.indexOf('. ');
    const statementLead =
        sentenceBreak === -1 ? heading : heading.slice(0, sentenceBreak + 1);
    const statementTail =
        sentenceBreak === -1 ? '' : heading.slice(sentenceBreak + 1);
    // Dark editorial theme (projects): heading flips to white and the Get Matched
    // buttons invert to a white fill; the grey eyebrow/description read on both.
    const buttonClass = isDark ? DARK_SELECTED : 'bg-[#323232] text-white';
    return (
        // Mobile treats the editorial copy as quiet meta, not a hero — small
        // type, tight eyebrow, so the category carousel owns the first
        // viewport. Desktop values unchanged via md:.
        // The injection variant's row bottom-aligns its two columns, so the
        // statement sits ~40px below the row's own top edge. This gap is only
        // the remainder that brings the eyebrow to 50px off that first line —
        // it reads much smaller than the space it actually produces.
        <div
            className={cn(
                'flex flex-col gap-2',
                isInjection ? 'md:gap-[0.7rem]' : 'md:gap-[2.375rem]',
            )}
        >
            {/* Eyebrow, injection variant only. The page lead drops it: the
                site header and the breadcrumb below already name the vertical,
                so it read as a third label for the same thing. The injections
                keep theirs — it introduces the Get Matched pitch rather than
                naming the page. `eyebrow` is still the source of the mobile
                lead label above. */}
            {isInjection ? (
                <p
                    className={cn(
                        'text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.046875rem] text-[#767676]',
                        hideMobileEyebrow && 'max-md:hidden',
                    )}
                >
                    {eyebrow}
                </p>
            ) : null}
            {/* The statement and the Get Matched rail share a row rather than
                the rail sitting against the whole left column — nested that
                way it would centre against the eyebrow too and float above the
                statement.
                Page lead: top-aligned, so the caption starts on the
                statement's first line. Injection: bottom-aligned, so the
                Get Matched button lands on the statement's last line instead
                of hanging below the block. */}
            {headingPreviewed ? (
                <span className="sr-only" role="status">
                    Previewing a candidate teaser from the URL; not what is live
                </span>
            ) : null}
            <div
                className={cn(
                    'flex flex-col gap-6 md:flex-row md:justify-between md:gap-10',
                    isInjection ? 'md:items-end' : 'md:items-start',
                )}
            >
                <div className={cn('min-w-0', statementMeasureClassName)}>
                    {isInjection ? (
                        <p
                            className={cn(
                                'text-[1.375rem] leading-[1.3] font-medium tracking-[-0.0515625rem] md:text-[2.0625rem] md:leading-[1.25]',
                                isDark ? 'text-offwhite' : 'text-[#323232]',
                            )}
                        >
                            {heading}
                        </p>
                    ) : (
                        /* Logged-out-homepage statement idiom on mobile: inline
                       "(Label)", first sentence in ink, the remainder in the
                       pale grey. Desktop names the category inline at the
                       statement's own weight. */
                        // A div rather than a p: the category name inside it
                        // is the page's h1 at md+, and a heading cannot live
                        // in a paragraph — the parser would close the p before
                        // it and split this statement in two. The p carried no
                        // margin of its own, so nothing moves.
                        <div
                            className={cn(
                                'font-sans text-[1.1256rem] leading-[1.2] font-normal tracking-[-0.03em]',
                                'md:text-[2.0625rem] md:leading-[1.25] md:font-medium md:tracking-[-0.028em]',
                                // Three lines is the space this statement gets
                                // on desktop, where the copy is written to fit
                                // with the category name ahead of it. The cut is
                                // hard, with no ellipsis: a visible truncation
                                // mark would advertise an overflow that the copy
                                // is written not to have, and there is no Read
                                // more. The clipped words stay in the markup.
                                // Mobile sets the
                                // copy at a smaller measure and lets it run.
                                // `lh` is the element's own line-height.
                                'md:max-h-[3lh] md:overflow-hidden',
                                isDark ? 'text-offwhite' : 'text-[#323232]',
                                statementClassName,
                            )}
                        >
                            {/* The page's one h1, at every width. Below md it
                                is the display heading above the statement (the
                                homepage "Discover" scale). At md+ it is the
                                statement's own first words — the category name,
                                inline and inheriting the statement's type — on
                                a category page; the directory roots and the
                                articles listing have no category to open with,
                                so there it steps out of the layout but stays
                                in the document, still carrying the page's name.
                                One element restyled per breakpoint rather than
                                two — a hidden second h1 is a second h1 to a
                                crawler. With hideMobileHeading a category page
                                reads it inline below md too, as desktop does;
                                a page with no category keeps it visually
                                hidden there. The " - " separator sits outside it so
                                the heading is the name alone. */}
                            <h1
                                className={cn(
                                    'm-0',
                                    // A page that opens on the mark and the
                                    // pill bar below md keeps the heading for
                                    // readers and crawlers only there.
                                    inlineMobileTitle
                                        ? 'max-md:inline max-md:[font-size:inherit] max-md:[line-height:inherit] max-md:[font-weight:inherit] max-md:[letter-spacing:inherit]'
                                        : hideMobileHeading
                                          ? 'max-md:sr-only'
                                          : 'max-md:mb-2 max-md:block max-md:font-sans max-md:text-[3.0326rem] max-md:leading-[3.0998rem] max-md:font-normal max-md:tracking-[-0.1268rem]',
                                    categoryPrefix
                                        ? 'md:inline md:[font-size:inherit] md:[line-height:inherit] md:[font-weight:inherit] md:[letter-spacing:inherit]'
                                        : 'md:sr-only',
                                )}
                            >
                                {introLabel}
                            </h1>
                            {categoryPrefix ? (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        !inlineMobileTitle && 'max-md:hidden',
                                    )}
                                >
                                    {' - '}
                                </span>
                            ) : null}
                            <span
                                className={cn(
                                    categoryPrefix &&
                                        (isDark
                                            ? 'md:text-offwhite/60'
                                            : 'md:text-[#86868b]'),
                                )}
                            >
                                {statementLead}
                            </span>
                            {statementTail ? (
                                <span
                                    className={cn(
                                        !mobileSingleTone &&
                                            (isDark
                                                ? 'max-md:text-offwhite/60'
                                                : 'max-md:text-[#767676]'),
                                        categoryPrefix &&
                                            (isDark
                                                ? 'md:text-offwhite/60'
                                                : 'md:text-[#86868b]'),
                                    )}
                                >
                                    {statementTail}
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
                {/* page-lead hides the rail below md: the Get Matched sections
                injected into the grid carry this CTA on mobile, flowing in as
                natural separators. The injection variant keeps it, tightened.

                The impression rides this element rather than the whole intro,
                because this is the part that can be pressed. Hidden here means
                no prompt was offered, and counting one would give the lead a
                mobile denominator it can never convert against. Reading it off
                the rendered box rather than a breakpoint in JS keeps the count
                honest at every width without a second copy of the rule. */}
                {rail ? (
                    <div className="flex flex-col items-start gap-[2.0625rem] max-md:hidden md:w-[27.8125rem] md:items-end">
                        {rail}
                    </div>
                ) : (
                    <div
                        ref={promptRef}
                        className={cn(
                            'flex flex-col items-start md:w-[27.8125rem] md:items-end',
                            isInjection
                                ? 'gap-4 md:gap-[2.0625rem]'
                                : 'gap-[2.0625rem] max-md:hidden',
                        )}
                    >
                        <div
                            className={cn(
                                'flex items-center gap-[0.6875rem] md:justify-end',
                                hideMobileCaption && 'max-md:hidden',
                            )}
                        >
                            {/* -0.02em, not the -0.05em the display headings use: that
                            much negative tracking closes up the counters at this
                            size. Expressed in em so it holds if the size changes. */}
                            <p className={INTRO_RAIL_CAPTION}>{caption}</p>
                            <button
                                type="button"
                                onClick={onPlus}
                                aria-label={
                                    explainer
                                        ? 'About Get Matched'
                                        : 'Get Matched'
                                }
                                className={cn(
                                    INTRO_RAIL_DISC,
                                    // The plus icon is desktop chrome — the labelled
                                    // Get Matched button below is the mobile CTA.
                                    isInjection && 'max-md:hidden',
                                    // Extend the tap target to ≥44px below md via an
                                    // invisible pseudo — the visual circle stays 42px.
                                    "max-md:before:absolute max-md:before:top-1/2 max-md:before:left-1/2 max-md:before:size-11 max-md:before:-translate-x-1/2 max-md:before:-translate-y-1/2 max-md:before:content-['']",
                                    buttonClass,
                                )}
                            >
                                {/* The design's icon is a light "times" cross rotated
                                -45° into a plus — the rotation gives it longer, more
                                elegant arms than a geometric plus. */}
                                <IntroPlusGlyph />
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={onGetMatched}
                            className={cn(
                                INTRO_RAIL_BUTTON,
                                isDark
                                    ? DARK_SELECTED
                                    : 'bg-[#323232] text-white',
                            )}
                        >
                            Get Matched
                        </button>
                    </div>
                )}
            </div>
            {explainer ? (
                <GetMatchedExplainerModal
                    open={explainerOpen}
                    onOpenChange={setExplainerOpen}
                    onContinue={() => {
                        setExplainerOpen(false);
                        onGetMatched();
                    }}
                    explainer={explainer}
                />
            ) : null}
        </div>
    );
};
